// Zoom +/-, Fit Board, layers and legend toggles, collapse. 44 px touch targets on phones.

import { cn } from '../lib/utils.js';
import type { MinimapLabels } from './types.js';

export interface MinimapToolbarProps {
  readonly labels: MinimapLabels;
  readonly onZoomIn: () => void;
  /** Draw the camera footprint; `undefined` hides the toggle entirely. */
  readonly showCameraBox?: boolean;
  readonly onToggleCameraBox?: () => void;
  readonly onZoomOut: () => void;
  readonly onFit: () => void;
  readonly onToggleLegend: () => void;
  readonly legendOpen: boolean;
  readonly legendId: string;
  readonly onToggleLayers: () => void;
  readonly layersOpen: boolean;
  readonly onCollapse: () => void;
  readonly zoomText: string;
  readonly className?: string;
}

const BTN =
  'inline-flex size-7 items-center justify-center rounded border border-border/60 bg-card/95 text-sm leading-none text-foreground transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring max-md:size-11 cursor-pointer disabled:cursor-default disabled:opacity-40';

export function MinimapToolbar({
  labels,
  onZoomIn,
  showCameraBox,
  onToggleCameraBox,
  onZoomOut,
  onFit,
  onToggleLegend,
  legendOpen,
  legendId,
  onToggleLayers,
  layersOpen,
  onCollapse,
  zoomText,
  className,
}: MinimapToolbarProps) {
  return (
    <div
      className={cn('flex items-center gap-1', className)}
      data-testid="minimap-toolbar"
      data-appdom-id="minimap-toolbar"
      data-appdom-type="Container"
      data-appdom-scope="aside"
      data-appdom-label={labels.title}
    >
      <span className="mr-0.5 min-w-8 text-right text-[10px] tabular-nums text-muted-foreground max-md:hidden">
        {zoomText}
      </span>
      <button
        type="button"
        className={BTN}
        aria-label={labels.zoomOut}
        title={labels.zoomOut}
        onClick={onZoomOut}
        data-testid="minimap-zoom-out"
        data-appdom-id="minimap-zoom-out"
        data-appdom-type="IconButton"
        data-appdom-scope="aside"
        data-appdom-label={labels.zoomOut}
      >
        <span aria-hidden="true">&minus;</span>
      </button>
      <button
        type="button"
        className={BTN}
        aria-label={labels.zoomIn}
        title={labels.zoomIn}
        onClick={onZoomIn}
        data-testid="minimap-zoom-in"
        data-appdom-id="minimap-zoom-in"
        data-appdom-type="IconButton"
        data-appdom-scope="aside"
        data-appdom-label={labels.zoomIn}
      >
        <span aria-hidden="true">+</span>
      </button>
      {onToggleCameraBox && (
        <button
          type="button"
          className={cn(BTN, showCameraBox === false && 'opacity-40')}
          aria-label={labels.cameraBox}
          aria-pressed={showCameraBox !== false}
          title={labels.cameraBox}
          onClick={onToggleCameraBox}
          data-testid="minimap-camera-box"
          data-appdom-id="minimap-camera-box"
          data-appdom-type="IconButton"
          data-appdom-scope="aside"
          data-appdom-label={labels.cameraBox}
        >
          {/* A dashed frame: the box it toggles, drawn as an outline. */}
          <svg
            viewBox="0 0 16 16"
            className="size-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M2 5V3.5A1.5 1.5 0 0 1 3.5 2H5M11 2h1.5A1.5 1.5 0 0 1 14 3.5V5M14 11v1.5A1.5 1.5 0 0 1 12.5 14H11M5 14H3.5A1.5 1.5 0 0 1 2 12.5V11" />
          </svg>
        </button>
      )}
      <button
        type="button"
        className={BTN}
        aria-label={labels.fit}
        title={labels.fit}
        onClick={onFit}
        data-testid="minimap-fit"
        data-appdom-id="minimap-fit"
        data-appdom-type="IconButton"
        data-appdom-scope="aside"
        data-appdom-label={labels.fit}
      >
        <svg
          viewBox="0 0 16 16"
          className="size-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4" />
        </svg>
      </button>
      <button
        type="button"
        className={cn(BTN, layersOpen && 'bg-accent')}
        aria-label={labels.layers}
        title={labels.layers}
        aria-expanded={layersOpen}
        onClick={onToggleLayers}
        data-testid="minimap-layers-toggle"
        data-appdom-id="minimap-layers-toggle"
        data-appdom-type="IconButton"
        data-appdom-scope="aside"
        data-appdom-label={labels.layers}
      >
        <svg
          viewBox="0 0 16 16"
          className="size-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M8 2 14 5.5 8 9 2 5.5ZM2 9.5 8 13l6-3.5" />
        </svg>
      </button>
      <button
        type="button"
        className={cn(BTN, legendOpen && 'bg-accent')}
        aria-label={labels.legend}
        title={labels.legend}
        aria-expanded={legendOpen}
        aria-controls={legendId}
        onClick={onToggleLegend}
        data-testid="minimap-legend-toggle"
        data-appdom-id="minimap-legend-toggle"
        data-appdom-type="IconButton"
        data-appdom-scope="aside"
        data-appdom-label={labels.legend}
      >
        <span aria-hidden="true">&#9432;</span>
      </button>
      <button
        type="button"
        className={BTN}
        aria-label={labels.collapse}
        title={labels.collapse}
        onClick={onCollapse}
        data-testid="minimap-collapse"
        data-appdom-id="minimap-collapse"
        data-appdom-type="IconButton"
        data-appdom-scope="aside"
        data-appdom-label={labels.collapse}
      >
        <svg
          viewBox="0 0 16 16"
          className="size-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M4 7l4 4 4-4" />
        </svg>
      </button>
    </div>
  );
}
